/**
 * HIPAA Compliance Hook
 * Handles session management, auto-logoff, and compliance features
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

const SESSION_TIMEOUT_MINUTES = 15;
const WARNING_THRESHOLD_MINUTES = 2;
const HEARTBEAT_INTERVAL_MS = 60000; // 1 minute

export const useHIPAACompliance = () => {
  const [sessionStatus, setSessionStatus] = useState({
    remainingMinutes: SESSION_TIMEOUT_MINUTES,
    showWarning: false,
    sessionExpired: false
  });
  const [hipaaStatus, setHipaaStatus] = useState({
    acknowledged: false,
    requiresAcknowledgment: false,
    passwordExpired: false,
    passwordExpiresInDays: 90,
    twoFactorEnabled: false
  });
  
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const heartbeatIntervalRef = useRef(null);
  const checkIntervalRef = useRef(null);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
    setSessionStatus(prev => ({
      ...prev,
      showWarning: false,
      remainingMinutes: SESSION_TIMEOUT_MINUTES
    }));
  }, []);

  // Send heartbeat to server
  const sendHeartbeat = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      await api.post('/auth/heartbeat');
    } catch (error) {
      console.error('Heartbeat failed:', error);
    }
  }, []);

  // Check session status
  const checkSession = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await api.get('/auth/session-status');
      
      if (response.data.success) {
        const { remainingMinutes, timeoutMinutes } = response.data;
        
        setSessionStatus(prev => ({
          ...prev,
          remainingMinutes,
          showWarning: remainingMinutes <= WARNING_THRESHOLD_MINUTES && !prev.sessionExpired
        }));
        
        if (remainingMinutes <= 0) {
          handleSessionExpired();
        }
      }
    } catch (error) {
      if (error.response?.status === 440) {
        handleSessionExpired();
      }
    }
  }, []);

  // Handle session expiration
  const handleSessionExpired = useCallback(() => {
    setSessionStatus(prev => ({
      ...prev,
      sessionExpired: true,
      showWarning: false
    }));
    
    // Clear token and redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('sessionExpired'));
  }, []);

  // Extend session (on user activity)
  const extendSession = useCallback(async () => {
    updateActivity();
    await sendHeartbeat();
  }, [updateActivity, sendHeartbeat]);

  // Logout
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  }, []);

  // Acknowledge HIPAA compliance
  const acknowledgeHIPAA = useCallback(async () => {
    try {
      const response = await api.post('/auth/acknowledge-hipaa');
      if (response.data.success) {
        setHipaaStatus(prev => ({
          ...prev,
          acknowledged: true,
          requiresAcknowledgment: false
        }));
        return { success: true };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  // Enable 2FA
  const enable2FA = useCallback(async () => {
    try {
      const response = await api.post('/auth/enable-2fa');
      return response.data;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  // Confirm 2FA
  const confirm2FA = useCallback(async (code) => {
    try {
      const response = await api.post('/auth/confirm-2fa', { code });
      if (response.data.success) {
        setHipaaStatus(prev => ({ ...prev, twoFactorEnabled: true }));
      }
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }, []);

  // Disable 2FA
  const disable2FA = useCallback(async (password) => {
    try {
      const response = await api.post('/auth/disable-2fa', { password });
      if (response.data.success) {
        setHipaaStatus(prev => ({ ...prev, twoFactorEnabled: false }));
      }
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }, []);

  // Change password
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail?.message || error.response?.data?.detail || error.message,
        errors: error.response?.data?.detail?.errors || []
      };
    }
  }, []);

  // Set up activity listeners
  useEffect(() => {
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      updateActivity();
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [updateActivity]);

  // Set up heartbeat and session check intervals
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Send heartbeat every minute
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    
    // Check session every 30 seconds
    checkIntervalRef.current = setInterval(checkSession, 30000);

    // Initial check
    checkSession();

    return () => {
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [sendHeartbeat, checkSession]);

  // Local timer for UI countdown
  useEffect(() => {
    const localTimerInterval = setInterval(() => {
      const elapsed = (Date.now() - lastActivityRef.current) / 1000 / 60;
      const remaining = Math.max(0, SESSION_TIMEOUT_MINUTES - elapsed);
      
      setSessionStatus(prev => ({
        ...prev,
        remainingMinutes: remaining,
        showWarning: remaining <= WARNING_THRESHOLD_MINUTES && remaining > 0
      }));

      if (remaining <= 0 && !sessionStatus.sessionExpired) {
        handleSessionExpired();
      }
    }, 1000);

    return () => clearInterval(localTimerInterval);
  }, [handleSessionExpired, sessionStatus.sessionExpired]);

  return {
    sessionStatus,
    hipaaStatus,
    setHipaaStatus,
    extendSession,
    logout,
    acknowledgeHIPAA,
    enable2FA,
    confirm2FA,
    disable2FA,
    changePassword,
    SESSION_TIMEOUT_MINUTES,
    WARNING_THRESHOLD_MINUTES
  };
};

export default useHIPAACompliance;
